/**
 * ModalGenerarRecordatorios.js
 * Modal de preview para generar recordatorios de medicamentos
 * Muestra resumen de lo que se creará antes de confirmar
 * Permite editar horarios sugeridos antes de generar
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { crearTratamiento, reemplazarTratamiento } from '../services/api';
import toast from 'react-hot-toast';
import ModalTratamientoDuplicado from './ModalTratamientoDuplicado';

// Calcular horarios inteligentes basados en frecuencia
const calcularHorariosInteligentes = (frecuenciaHoras) => {
  switch (frecuenciaHoras) {
    case 24:
      return ['08:00'];
    case 12:
      return ['08:00', '20:00'];
    case 8:
      return ['06:00', '14:00', '22:00'];
    case 6:
      return ['06:00', '12:00', '18:00', '00:00'];
    case 4:
      return ['06:00', '10:00', '14:00', '18:00', '22:00', '02:00'];
    default:
      // Para otras frecuencias, calcular dinámicamente
      const horas = [];
      let hora = 6; // Empezar a las 6 AM
      const tomasAlDia = Math.ceil(24 / frecuenciaHoras);
      for (let i = 0; i < tomasAlDia; i++) {
        horas.push(`${String(hora % 24).padStart(2, '0')}:00`);
        hora += frecuenciaHoras;
      }
      return horas;
  }
};

// Calcular siguiente hora sugerida
const calcularSiguienteHora = (horasExistentes) => {
  if (horasExistentes.length === 0) return '08:00';
  const horasPreferidas = ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00'];
  for (const hora of horasPreferidas) {
    if (!horasExistentes.includes(hora)) {
      return hora;
    }
  }
  return '00:00';
};

export default function ModalGenerarRecordatorios({
  show,
  onClose,
  medicamentos: medicamentosInput,
  objetoId,
  entradaExpedienteId,
  onSuccess
}) {
  const navigate = useNavigate();
  const [medicamentos, setMedicamentos] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showDuplicadoModal, setShowDuplicadoModal] = useState(false);
  const [tratamientoExistenteId, setTratamientoExistenteId] = useState(null);
  const [reemplazando, setReemplazando] = useState(false);

  // Inicializar medicamentos con horarios inteligentes
  useEffect(() => {
    if (show && medicamentosInput?.length > 0) {
      const medsConHorarios = medicamentosInput.map(med => ({
        ...med,
        horasSugeridas: med.horasSugeridas || calcularHorariosInteligentes(med.frecuenciaHoras || 12)
      }));
      setMedicamentos(medsConHorarios);
    }
  }, [show, medicamentosInput]);

  // Handlers para editar horarios
  const handleAddHora = (medIndex) => {
    setMedicamentos(prev => {
      const meds = [...prev];
      const horasActuales = meds[medIndex].horasSugeridas || [];
      const nuevaHora = calcularSiguienteHora(horasActuales);
      meds[medIndex] = { ...meds[medIndex], horasSugeridas: [...horasActuales, nuevaHora].sort() };
      return meds;
    });
  };

  const handleRemoveHora = (medIndex, horaIndex) => {
    setMedicamentos(prev => {
      const meds = [...prev];
      const horasActuales = meds[medIndex].horasSugeridas || [];
      meds[medIndex] = {
        ...meds[medIndex],
        horasSugeridas: horasActuales.filter((_, i) => i !== horaIndex)
      };
      return meds;
    });
  };

  const handleChangeHora = (medIndex, horaIndex, newValue) => {
    setMedicamentos(prev => {
      const meds = [...prev];
      const horasActuales = [...(meds[medIndex].horasSugeridas || [])];
      horasActuales[horaIndex] = newValue;
      horasActuales.sort();
      meds[medIndex] = { ...meds[medIndex], horasSugeridas: horasActuales };
      return meds;
    });
  };

  // Calcular totales
  const totalRecordatorios = medicamentos.reduce((sum, med) => {
    const tomasAlDia = (med.horasSugeridas || []).length;
    return sum + (tomasAlDia * (med.duracionDias || 7));
  }, 0);

  const diasTotales = Math.max(...medicamentos.map(m => m.duracionDias || 7), 0);

  async function handleCrear() {
    setCreating(true);

    const result = await crearTratamiento({
      objetoId,
      entradaExpedienteId,
      medicamentos
    });

    setCreating(false);

    if (result.error) {
      // Manejar error de tratamiento duplicado
      if (result.error === "TRATAMIENTO_DUPLICADO" && result.tratamientoExistenteId) {
        setTratamientoExistenteId(result.tratamientoExistenteId);
        setShowDuplicadoModal(true);
        return;
      }
      toast.error(result.message || result.error);
      return;
    }

    toast.success(`${result.totalRecordatorios} recordatorios creados`);

    if (onSuccess) {
      onSuccess(result);
    }

    onClose();
  }

  function handleVerActual(tratamientoId) {
    setShowDuplicadoModal(false);
    onClose();
    navigate(`/tratamientos/${tratamientoId}`);
  }

  async function handleReemplazar() {
    setReemplazando(true);

    const result = await reemplazarTratamiento({
      objetoId,
      entradaExpedienteId,
      medicamentos
    });

    setReemplazando(false);
    setShowDuplicadoModal(false);

    if (result.error) {
      toast.error(result.error);
      return;
    }

    toast.success(`Tratamiento actualizado: ${result.totalRecordatorios} recordatorios creados`);

    if (onSuccess) {
      onSuccess(result);
    }

    onClose();
  }

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100001,
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(to bottom, #e8f5c8, #c5e8a0)',
            padding: '20px 24px',
            color: '#333'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              🔔 Crear recordatorios
            </h3>
            <button
              onClick={onClose}
              style={{
                background: '#4CAF50',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                fontSize: '20px'
              }}
            >
              &times;
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {medicamentos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
              No hay medicamentos para crear recordatorios
            </div>
          ) : (
            <>
              {/* Lista de medicamentos */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', color: '#666', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Medicamentos ({medicamentos.length})
                </h4>

                {medicamentos.map((med, idx) => {
                  const tomasAlDia = (med.horasSugeridas || []).length;
                  const totalRec = tomasAlDia * (med.duracionDias || 7);

                  return (
                    <div
                      key={idx}
                      style={{
                        backgroundColor: '#f9fafb',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '12px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#111', marginBottom: '4px' }}>
                            💊 {med.nombre}
                          </div>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            {med.dosis} • Cada {med.frecuenciaHoras}h • {med.duracionDias} días
                          </div>
                        </div>
                        <div style={{
                          backgroundColor: '#00BCD4',
                          color: '#fff',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {totalRec} rec.
                        </div>
                      </div>

                      {/* Horarios editables */}
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        border: '1px solid #90caf9'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: '#1565c0', fontWeight: '500' }}>
                              🕐 Horarios de toma
                            </span>
                            <span style={{ fontSize: '10px', color: '#1976d2', backgroundColor: '#bbdefb', padding: '2px 6px', borderRadius: '4px' }}>
                              ✨ Sugeridos
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddHora(idx)}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              backgroundColor: '#fff',
                              color: '#1976d2',
                              cursor: 'pointer',
                              fontSize: '11px'
                            }}
                          >
                            + Agregar
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                          {(med.horasSugeridas || []).map((hora, hIdx) => (
                            <div
                              key={hIdx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                padding: '4px 4px 4px 8px',
                                border: '1px solid #90caf9'
                              }}
                            >
                              <input
                                type="time"
                                value={hora}
                                onChange={(e) => handleChangeHora(idx, hIdx, e.target.value)}
                                style={{
                                  border: 'none',
                                  padding: '2px 4px',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  color: '#1565c0',
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  width: '85px'
                                }}
                              />
                              {(med.horasSugeridas || []).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveHora(idx, hIdx)}
                                  style={{
                                    border: 'none',
                                    backgroundColor: '#ffcdd2',
                                    color: '#c62828',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0
                                  }}
                                  title="Eliminar horario"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        <p style={{ fontSize: '10px', color: '#1976d2', margin: '6px 0 0', opacity: 0.8 }}>
                          Puedes editar los horarios antes de generar los recordatorios
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Resumen */}
              <div
                style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '24px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#166534' }}>Total de recordatorios:</span>
                  <strong style={{ color: '#166534' }}>{totalRecordatorios}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#166534' }}>Duración del tratamiento:</span>
                  <strong style={{ color: '#166534' }}>{diasTotales} días</strong>
                </div>
              </div>

              {/* Explicación */}
              <div
                style={{
                  backgroundColor: '#E0F7FA',
                  border: '1px solid #80DEEA',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '13px',
                  color: '#006064'
                }}
              >
                <strong style={{ display: 'block', marginBottom: '8px' }}>¿Cómo funciona?</strong>
                <p style={{ margin: 0 }}>
                  El sistema registrará automáticamente cada día. Solo avísanos si olvidas algo usando el botón "Olvidé algo hoy" en tu dashboard.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}
        >
          <button
            onClick={onClose}
            disabled={creating}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              backgroundColor: '#fff',
              cursor: creating ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleCrear}
            disabled={creating || medicamentos.length === 0}
            style={{
              padding: '10px 24px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: creating || medicamentos.length === 0 ? '#d1d5db' : '#4CAF50',
              color: '#fff',
              cursor: creating || medicamentos.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {creating ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status"></span>
                Creando...
              </>
            ) : (
              '✨ Crear recordatorios'
            )}
          </button>
        </div>
      </div>

      {/* Modal de duplicado */}
      <ModalTratamientoDuplicado
        show={showDuplicadoModal}
        onClose={() => setShowDuplicadoModal(false)}
        tratamientoExistenteId={tratamientoExistenteId}
        onVerActual={handleVerActual}
        onReemplazar={handleReemplazar}
        reemplazando={reemplazando}
      />
    </div>
  );
}
